import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FinancialReportingByVendorRow } from '@monorepo/interfaces'
import { http } from '@monorepo/infra'
import { format } from 'date-fns'
import * as S from '../styles'
import { PageSpinner } from '@monorepo/components'
import * as FileSaver from 'file-saver'
import XLSX from 'sheetjs-style'

interface FinancialReportingByVendorProps {
  vendor: number
  startDate: Date
  endDate: Date
}

const FinancialReportingByVendor: React.FC<FinancialReportingByVendorProps> = ({
  vendor,
  startDate,
  endDate,
}) => {
  const [report, setReport] = useState<FinancialReportingByVendorRow[] | null>(
    null
  )
  const formatter = useMemo(() => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    })
  }, [])
  const exportExcel = useCallback(async () => {
    if (report !== null) {
      const reportData = report.map((reportRow) => {
        return {
          'Product Name': reportRow.productName,
          Type: reportRow.type,
          'Media ID': reportRow.mediaId,
          'Order #': reportRow.orderNumber,
          Date: format(new Date(reportRow.date), 'MM/dd/yyyy'),
          'Dealer ID': reportRow.dealerId,
          'Dealer Name': reportRow.dealerName,
          'Co-Op': formatter.format(reportRow.coop),
          Spend: formatter.format(reportRow.spend),
          Total: formatter.format(reportRow.total),
        }
      })
      const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset-UTF-8'
      const fileExtension = '.xlsx'
      const ws = XLSX.utils.json_to_sheet(reportData)
      const wb = { Sheets: { Sheet1: ws }, SheetNames: ['Sheet1'] }
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const data = new Blob([excelBuffer], { type: fileType })
      FileSaver.saveAs(data, `FinancialReportByVendor${fileExtension}`)
    }
  }, [formatter, report])
  const getVendorNameFromId = (id: number | null): string => {
    if (id === null) return 'UNKNOWN'
    switch (id) {
      case 1:
        return 'PURE'
      case 2:
        return 'DSI'
      default:
        return 'UNKNOWN'
    }
  }
  useEffect(() => {
    http
      .get<FinancialReportingByVendorRow[]>({
        url: `/reports/financialReportingByVendor?vendor=${vendor}&dateFrom=${format(
          startDate,
          'yyyy-MM-dd'
        )}&dateTo=${format(endDate, 'yyyy-MM-dd')}`,
      })
      .then(({ data }) => setReport(data))
  }, [endDate, startDate, vendor])
  if (!report || report === null) {
    return <PageSpinner />
  }
  const totalSales = report.map((item) => item.total).reduce((a, b) => a + b, 0)
  const totalCoop = report.map((item) => item.coop).reduce((a, b) => a + b, 0)
  const totalSpend = report.map((item) => item.spend).reduce((a, b) => a + b, 0)
  return (
    <S.Container>
      <S.ReportTitle>
        Financial Reporting
        <S.ExcelButton
          label="Download Excel"
          colorOption="blue"
          onClick={exportExcel}
        />
      </S.ReportTitle>
      <S.FilterList>
        <S.FilterListItem>
          <S.FilterListItemTitle>Vendor</S.FilterListItemTitle>
          <S.FilterListItemValue>
            {getVendorNameFromId(vendor)}
          </S.FilterListItemValue>
          <S.StyledIcon icon="FourPlusRectangle" />
        </S.FilterListItem>
        <S.FilterListItem>
          <S.FilterListItemTitle>Time Period</S.FilterListItemTitle>
          <S.FilterListItemValue>
            {`${format(startDate, 'MM/dd/yyyy')} - ${format(
              endDate,
              'MM/dd/yyyy'
            )}`}
          </S.FilterListItemValue>
          <S.StyledIcon icon="FourPlusRectangle" />
        </S.FilterListItem>
      </S.FilterList>
      <S.ReportTitle>Financial Reporting</S.ReportTitle>
      {report.length > 0 && (
        <>
          <S.TableContainer>
            <S.Table>
              <S.TableHead>
                <S.TableRow>
                  <S.TableHeadCell>Product Name</S.TableHeadCell>
                  <S.TableHeadCell>Type</S.TableHeadCell>
                  <S.TableHeadCell>Media ID</S.TableHeadCell>
                  <S.TableHeadCell>Order #</S.TableHeadCell>
                  <S.TableHeadCell>Date</S.TableHeadCell>
                  <S.TableHeadCell>Dealer ID</S.TableHeadCell>
                  <S.TableHeadCell>Dealer Name</S.TableHeadCell>
                  <S.TableHeadCell>Co-Op</S.TableHeadCell>
                  <S.TableHeadCell>Spend</S.TableHeadCell>
                  <S.TableHeadCell>Total</S.TableHeadCell>
                </S.TableRow>
              </S.TableHead>
              <S.TableBody>
                {report.map(
                  (item: FinancialReportingByVendorRow, index: number) => (
                    <S.TableRow key={`row${index}`}>
                      <S.TableCell>{item.productName}</S.TableCell>
                      <S.TableCell>{item.type}</S.TableCell>
                      <S.TableCell>{item.mediaId}</S.TableCell>
                      <S.TableCell>{item.orderNumber}</S.TableCell>
                      <S.TableCell>
                        {format(new Date(item.date), 'MM/dd/yyyy')}
                      </S.TableCell>
                      <S.TableCell>{item.dealerId}</S.TableCell>
                      <S.TableCell>{item.dealerName}</S.TableCell>
                      <S.TableCell>{formatter.format(item.coop)}</S.TableCell>
                      <S.TableCell>{formatter.format(item.spend)}</S.TableCell>
                      <S.TableCell>{formatter.format(item.total)}</S.TableCell>
                    </S.TableRow>
                  )
                )}
              </S.TableBody>
              <S.TableFooter>
                <S.TableRow>
                  <S.TableFootCell>Totals</S.TableFootCell>
                  <S.TableFootCell></S.TableFootCell>
                  <S.TableFootCell></S.TableFootCell>
                  <S.TableFootCell></S.TableFootCell>
                  <S.TableFootCell></S.TableFootCell>
                  <S.TableFootCell></S.TableFootCell>
                  <S.TableFootCell></S.TableFootCell>
                  <S.TableFootCell>
                    {formatter.format(totalCoop)}
                  </S.TableFootCell>
                  <S.TableFootCell>
                    {formatter.format(totalSpend)}
                  </S.TableFootCell>
                  <S.TableFootCell>
                    {formatter.format(totalSales)}
                  </S.TableFootCell>
                </S.TableRow>
              </S.TableFooter>
            </S.Table>
          </S.TableContainer>
        </>
      )}
    </S.Container>
  )
}

export default FinancialReportingByVendor
